/**
 * ConversationNotesPanel Component - Phase 6A: Internal Agent Communication
 * 
 * Provides a comprehensive interface for agents to create, view, edit, and manage
 * internal notes for conversations. Supports different note types, visibility levels,
 * and real-time collaboration following existing UI patterns.
 */

import React, { useState, useCallback } from 'react'
import { 
  MessageSquare, 
  Plus, 
  Pin, 
  Edit3, 
  Trash2, 
  AlertTriangle, 
  Users, 
  Eye, 
  EyeOff,
  Clock,
  Flag
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

import { useConversationNotes } from '../../../hooks/useConversationNotes'
import { useAuth } from '../../../hooks/useAuth'
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent 
} from '../../ui/Card'
import { Button } from '../../ui/Button'
import { Textarea } from '../../ui/Textarea'
import { Badge } from '../../ui/Badge'
import { Modal } from '../../ui/Modal'
import { NativeSelect } from '../../ui/NativeSelect'
import { logger } from '../../../utils/logger'

import type { 
  ConversationNote, 
  CreateConversationNoteRequest,
  UpdateConversationNoteRequest 
} from '../../../types'

export interface ConversationNotesPanelProps {
  conversationId: string
  onNotesUpdate?: (totalNotes: number) => void
  className?: string
}

interface NoteFormState {
  content: string
  note_type: 'internal' | 'handoff' | 'escalation' | 'follow_up'
  visibility: 'team' | 'all_agents' | 'private' | 'supervisors_only'
  priority: 'urgent' | 'high' | 'normal' | 'low'
  is_pinned: boolean
}

const defaultFormState: NoteFormState = {
  content: '',
  note_type: 'internal',
  visibility: 'team',
  priority: 'normal',
  is_pinned: false
}

export const ConversationNotesPanel: React.FC<ConversationNotesPanelProps> = ({
  conversationId,
  onNotesUpdate,
  className = ''
}) => {
  const { user } = useAuth()
  const {
    notes,
    isLoading,
    isCreating,
    error,
    totalCount,
    createNote,
    updateNote,
    deleteNote,
    refreshNotes,
    clearError
  } = useConversationNotes({
    conversationId,
    autoFetch: true,
    onError: (error) => logger.error('Notes panel error', { error }),
    onNoteCreated: () => onNotesUpdate?.(totalCount + 1),
    onNoteDeleted: () => onNotesUpdate?.(totalCount - 1)
  })

  // UI State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [formState, setFormState] = useState<NoteFormState>(defaultFormState)
  const [formError, setFormError] = useState<string | null>(null)

  /**
   * Handle form input changes
   */
  const handleFormChange = useCallback((
    field: keyof NoteFormState,
    value: string | boolean
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }))
    setFormError(null) // Clear form errors when user types
  }, [])

  /**
   * Validate form data
   */
  const validateForm = useCallback((data: NoteFormState): string | null => {
    if (!data.content.trim()) {
      return 'Note content is required'
    }
    if (data.content.length > 5000) {
      return 'Note content must be 5000 characters or less'
    }
    return null
  }, [])

  /**
   * Handle creating a new note
   */
  const handleCreateNote = useCallback(async () => {
    const validationError = validateForm(formState)
    if (validationError) {
      setFormError(validationError)
      return
    }

    const noteData: Omit<CreateConversationNoteRequest, 'conversation_id'> = {
      content: formState.content.trim(),
      note_type: formState.note_type,
      visibility: formState.visibility,
      priority: formState.priority,
      is_pinned: formState.is_pinned
    }

    const success = await createNote(noteData)
    if (success) {
      setIsCreateFormOpen(false)
      setFormState(defaultFormState)
      setFormError(null)
    }
  }, [formState, validateForm, createNote])

  /**
   * Handle updating an existing note
   */
  const handleUpdateNote = useCallback(async (noteId: string) => {
    const validationError = validateForm(formState)
    if (validationError) {
      setFormError(validationError)
      return
    }

    const updateData: UpdateConversationNoteRequest = {
      content: formState.content.trim(),
      note_type: formState.note_type,
      visibility: formState.visibility,
      priority: formState.priority,
      is_pinned: formState.is_pinned
    }

    const success = await updateNote(noteId, updateData)
    if (success) {
      setEditingNoteId(null)
      setFormState(defaultFormState)
      setFormError(null)
    }
  }, [formState, validateForm, updateNote])

  /**
   * Handle starting to edit a note
   */
  const handleStartEdit = useCallback((note: ConversationNote) => {
    setFormState({
      content: note.content,
      note_type: note.note_type,
      visibility: note.visibility,
      priority: note.priority,
      is_pinned: note.is_pinned
    })
    setEditingNoteId(note.id)
    setFormError(null)
  }, [])

  /**
   * Handle canceling edit
   */
  const handleCancelEdit = useCallback(() => {
    setEditingNoteId(null)
    setFormState(defaultFormState)
    setFormError(null)
  }, [])

  /**
   * Handle deleting a note
   */
  const handleDeleteNote = useCallback(async (noteId: string) => {
    if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      await deleteNote(noteId)
    }
  }, [deleteNote])

  /**
   * Check if user can edit/delete a note
   */
  const canEditNote = useCallback((note: ConversationNote): boolean => {
    return note.agent_id === user?.id || user?.user_type === 'company_owner'
  }, [user])

  /**
   * Get badge variant based on priority
   */
  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'destructive'
      case 'high': return 'default'
      case 'normal': return 'secondary'
      case 'low': return 'outline'
      default: return 'secondary'
    }
  }

  /**
   * Get icon for note type
   */
  const getNoteTypeIcon = (noteType: string) => {
    switch (noteType) {
      case 'handoff': return <Users className="w-3 h-3" />
      case 'escalation': return <AlertTriangle className="w-3 h-3" />
      case 'follow_up': return <Clock className="w-3 h-3" />
      default: return <MessageSquare className="w-3 h-3" />
    }
  }

  /**
   * Get visibility icon
   */
  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'private': return <EyeOff className="w-3 h-3" />
      case 'supervisors_only': return <Flag className="w-3 h-3" />
      default: return <Eye className="w-3 h-3" />
    }
  }

  return (
    <Card className={`h-full ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-4 h-4" />
            Internal Notes
            {totalCount > 0 && (
              <Badge variant="secondary" className="ml-1">
                {totalCount}
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsCreateFormOpen(true)}
            disabled={isCreating}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Note
          </Button>
        </div>
      </CardHeader>

      <CardContent className="h-full p-4 space-y-4">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex justify-between items-start">
              <p className="text-sm text-red-700">{error}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearError}
                className="text-red-700 hover:text-red-900"
              >
                ×
              </Button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && notes.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">Loading notes...</div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && notes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <MessageSquare className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-500 mb-1">No notes yet</p>
            <p className="text-xs text-gray-400">Add internal notes to collaborate with your team</p>
          </div>
        )}

        {/* Notes List */}
        {notes.length > 0 && (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {notes.map((note) => (
              <div
                key={note.id}
                className={`border rounded-lg p-3 space-y-2 ${
                  note.is_pinned ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                }`}
              >
                {/* Note Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {note.is_pinned && <Pin className="w-3 h-3 text-blue-600 flex-shrink-0" />}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {getNoteTypeIcon(note.note_type)}
                      <Badge variant="outline" className="text-xs">
                        {note.note_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {getVisibilityIcon(note.visibility)}
                      <Badge 
                        variant={getPriorityVariant(note.priority)}
                        className="text-xs"
                      >
                        {note.priority}
                      </Badge>
                    </div>
                  </div>
                  
                  {canEditNote(note) && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit(note)}
                        className="h-6 w-6 p-0"
                      >
                        <Edit3 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNote(note.id)}
                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Note Content */}
                {editingNoteId === note.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={formState.content}
                      onChange={(e) => handleFormChange('content', e.target.value)}
                      placeholder="Note content..."
                      className="min-h-20"
                      maxLength={5000}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <NativeSelect
                          value={formState.note_type}
                          onChange={(e) => handleFormChange('note_type', e.target.value)}
                          className="text-xs"
                        >
                          <option value="internal">Internal</option>
                          <option value="handoff">Handoff</option>
                          <option value="escalation">Escalation</option>
                          <option value="follow_up">Follow-up</option>
                        </NativeSelect>
                        <NativeSelect
                          value={formState.priority}
                          onChange={(e) => handleFormChange('priority', e.target.value)}
                          className="text-xs"
                        >
                          <option value="urgent">Urgent</option>
                          <option value="high">High</option>
                          <option value="normal">Normal</option>
                          <option value="low">Low</option>
                        </NativeSelect>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleUpdateNote(note.id)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                    {formError && (
                      <p className="text-xs text-red-600">{formError}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.content}</p>
                  </div>
                )}

                {/* Note Footer */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <span>{note.agent_name || 'Unknown Agent'}</span>
                    {note.is_edited && (
                      <Badge variant="outline" className="text-xs">
                        edited
                      </Badge>
                    )}
                  </div>
                  <span>{formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Note Modal */}
        <Modal
          isOpen={isCreateFormOpen}
          onClose={() => setIsCreateFormOpen(false)}
          title="Add Internal Note"
          size="md"
        >
          <div className="space-y-4">
            <Textarea
              value={formState.content}
              onChange={(e) => handleFormChange('content', e.target.value)}
              placeholder="Enter your note content..."
              className="min-h-32"
              maxLength={5000}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note Type
                </label>
                <NativeSelect
                  value={formState.note_type}
                  onChange={(e) => handleFormChange('note_type', e.target.value)}
                >
                  <option value="internal">Internal</option>
                  <option value="handoff">Handoff</option>
                  <option value="escalation">Escalation</option>
                  <option value="follow_up">Follow-up</option>
                </NativeSelect>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <NativeSelect
                  value={formState.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                >
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </NativeSelect>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visibility
                </label>
                <NativeSelect
                  value={formState.visibility}
                  onChange={(e) => handleFormChange('visibility', e.target.value)}
                >
                  <option value="team">Team</option>
                  <option value="all_agents">All Agents</option>
                  <option value="private">Private</option>
                  <option value="supervisors_only">Supervisors Only</option>
                </NativeSelect>
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 pt-6">
                  <input
                    type="checkbox"
                    checked={formState.is_pinned}
                    onChange={(e) => handleFormChange('is_pinned', e.target.checked)}
                    className="rounded"
                  />
                  Pin to top
                </label>
              </div>
            </div>

            {formError && (
              <p className="text-sm text-red-600">{formError}</p>
            )}

            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsCreateFormOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleCreateNote}
                disabled={isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Note'}
              </Button>
            </div>
          </div>
        </Modal>
      </CardContent>
    </Card>
  )
}